// @flow

import React, { useMemo, useRef, useState } from "react";
import { localize } from "redux-i18n";
import { type PropertiesSchema } from "@types";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  SchemaForm,
  Text,
  View,
} from "@components";
import type { EmployeeAbsenceManagerNotes } from "@modules/shared/reducer/employee-absences/types";
import uiSchema from "./ui-schema";

type Props = $Exact<{
  editMode: boolean,
  data: ?EmployeeAbsenceManagerNotes,
  onDismissHandler: () => void,
  onSubmit: (form: Object) => void,
  schema: ?PropertiesSchema,
  ...PropsFromLocalize,
}>;

const AddManagerNotesModal = (props: Props) => {
  const [formData, setFormData] = useState(props.data);

  const formRef = useRef(null);

  const submitForm = () => {
    formRef.current.submit();
  };

  const handleSubmit = (form: EmployeeAbsenceManagerNotes) => {
    props.onSubmit(form);
  };

  const handleFormChange = (data: EmployeeAbsenceManagerNotes) => {
    setFormData(data);
  };

  const isFitNoteSelected = useMemo(() => {
    if (!props.schema) return false;

    const absenceNoteOptions = props.schema.properties?.noteType?.properties?.id?.oneOf || [];
    const fitNoteOption = absenceNoteOptions.find(el => el.title === "Fit Note");

    return formData?.noteType && fitNoteOption && formData.noteType.id === fitNoteOption.const;
  }, [formData, props.schema]);

  return (
    <Modal className="modal-normal">
      <ModalHeader toggle={props.onDismissHandler}>
        {props.t(props.editMode ? "EDIT_MANAGER_NOTE" : "ADD_MANAGER_NOTE")}
      </ModalHeader>
      <ModalBody>
        {props.schema && (
          <SchemaForm
            ref={formRef}
            schema={props.schema}
            onSubmit={handleSubmit}
            uiSchema={uiSchema}
            formData={formData}
            onChange={handleFormChange}
            externalSubmit
          />
        )}
        {isFitNoteSelected && (
          <View direction="row" justifyContent="space-between" padding="0.5rem 0 0.5rem 0.5rem">
            <View flex={1} />
            <View flex={3} className="message-box">
              <Text html={props.t("FIT_NOTE_WARNING")} />
            </View>
          </View>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.onDismissHandler}>
          {props.t("CANCEL")}
        </Button>
        <Button materialIcon={props.editMode ? "save" : "add_box"} onClick={submitForm}>
          {props.t(props.editMode ? "SAVE" : "ADD")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default localize()(AddManagerNotesModal);
